import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import SlideOver from '../SlideOver/SlideOver';
import PriceField from '../PriceField/PriceField';
import { ButtonV2 } from '../ButtonV2/ButtonV2';
import { Plus, Minus, Calendar, Check, X, Edit, Eye } from 'lucide-react';
import CustomDatePicker from '../CustomDatePicker/CustomDatePicker';
import SelectInput from '../SelectInput/SelectInput';
import TextOptionalActionButton from '../TextOptionalActionButton/TextOptionalActionButton';
import InputShortText from '../InputShortText/InputShortText';
import InputNumber from '../InputNumber/InputNumber';
import { Skeleton } from '../Skeleton/Skeleton';
import Tooltip from '../Tooltip/Tooltip';
import PaymentPlansAPI, { PaymentPlanDto, CreatePaymentPlanRequest, UpdatePaymentPlanRequest, DefaultPaymentPlanData } from '../../api/PaymentPlansAPI';
import { format } from 'date-fns';
import { paymentMethodTypeToTranslationKey } from '../../shared/paymentMethodToTranslationKey';
import { paymentPlanStatusToTranslationKey, paymentPlanStatusToPillColor } from '../../utils/payment-plan-status';

interface PaymentSplit {
  id: string;
  amount: number;
  dueDate?: string;
  paymentMethod: 'TRANSFER' | 'CARD' | 'CASH';
  paid?: boolean;
  paidDate?: string;
  invoiceReference?: string;
  showDueDateField?: boolean;
  showInvoiceField?: boolean;
}

interface PaymentPlanManagerProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  orderId?: string;
  itemName?: string;
  onPaymentPlanUpdated?: () => void;
  aboveModal?: boolean;
}

const PaymentPlanManager: React.FC<PaymentPlanManagerProps> = ({
  isOpen,
  setIsOpen,
  orderId,
  itemName,
  onPaymentPlanUpdated,
  aboveModal = false,
}) => {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(1);
  const [totalAmount, setTotalAmount] = useState(0);
  const [currency, setCurrency] = useState('EUR');
  const [numberOfSplits, setNumberOfSplits] = useState(2);
  const [defaultData, setDefaultData] = useState<DefaultPaymentPlanData | null>(null);
  
  // Loading and data states
  const [isLoading, setIsLoading] = useState(false);
  const [existingPaymentPlan, setExistingPaymentPlan] = useState<PaymentPlanDto | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Calculate initial splits
  const [paymentSplits, setPaymentSplits] = useState<PaymentSplit[]>([]);

  const fetchDefaultData = useCallback(async () => {
    if (!orderId) return;
    
    try {
      const response = await PaymentPlansAPI.getDefaultPaymentPlanData(orderId);
      setDefaultData(response.data);
      setTotalAmount(response.data.price);
      setCurrency(response.data.currency);
      
      const exactSplitAmount = response.data.price / 2;
      const defaultSplits: PaymentSplit[] = [
        { 
          id: '1', 
          amount: Math.round(exactSplitAmount * 100) / 100,
          paymentMethod: 'TRANSFER',
          paid: false,
          showDueDateField: false,
          showInvoiceField: false
        },
        { 
          id: '2', 
          amount: Math.round(exactSplitAmount * 100) / 100,
          paymentMethod: 'TRANSFER',
          paid: false,
          showDueDateField: false,
          showInvoiceField: false
        }
      ];
      setPaymentSplits(defaultSplits);
    } catch (err: any) {
      console.warn('Failed to load default payment plan data:', err);
      // Use fallback values
      setTotalAmount(0);
      setCurrency('EUR');
      setPaymentSplits([]);
    }
  }, [orderId]);

  const fetchPaymentPlan = useCallback(async () => {
    if (!orderId) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await PaymentPlansAPI.getPaymentPlan(orderId);
      
      // Check if response data is empty or null
      if (!response.data || !response.data.id) {
        // No payment plan exists, this is expected
        setExistingPaymentPlan(null);
        // Ensure default data is loaded when no payment plan exists
        if (!defaultData) {
          await fetchDefaultData();
        }
      } else {
        setExistingPaymentPlan(response.data);
        // Initialize local state with existing data
        setTotalAmount(response.data.totalAmount);
        setCurrency(response.data.currency);
        setNumberOfSplits(response.data.paymentSplits.length);
        
        // Convert API splits to local format
        const localSplits: PaymentSplit[] = response.data.paymentSplits.map((split, index) => ({
          id: String(split.id || index + 1),
          amount: split.amount,
          dueDate: split.dueDate,
          paymentMethod: split.paymentMethod,
          paid: split.paid,
          paidDate: split.paidDate,
          invoiceReference: split.invoiceReference,
          showDueDateField: false,
          showInvoiceField: false
        }));
        setPaymentSplits(localSplits);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || t('payment_plan.errors.failed_to_load'));
    } finally {
      setIsLoading(false);
    }
  }, [orderId, defaultData, fetchDefaultData, t]);

  // Fetch payment plan when slider opens
  useEffect(() => {
    if (isOpen && orderId) {
      fetchPaymentPlan();
    }
  }, [isOpen, orderId, fetchPaymentPlan]);

  // Initialize default data when component mounts
  useEffect(() => {
    if (orderId && !defaultData) {
      fetchDefaultData();
    }
  }, [orderId, defaultData, fetchDefaultData]);

  const currencies = ['EUR', 'USD', 'PLN'];
  const paymentMethods = [
    { value: 'CARD', readableValue: t('payment.methods.card') },
    { value: 'TRANSFER', readableValue: t('payment.methods.transfer') },
    { value: 'CASH', readableValue: t('payment.methods.cash') }
  ];

  const handleCurrencyChange = (newCurrency: string) => {
    setCurrency(newCurrency);
  };

  const handlePriceChange = (newPrice: number) => {
    setTotalAmount(newPrice);
    const totalCents = Math.round(newPrice * 100);
    const baseCents = Math.floor(totalCents / numberOfSplits);
    const remainderCents = totalCents - baseCents * numberOfSplits;

    const newSplits = paymentSplits.map((split, index) => ({
      ...split,
      amount: ((baseCents + (index < remainderCents ? 1 : 0)) / 100)
    }));
    setPaymentSplits(newSplits);
  };

  const handleNumberOfSplitsChange = (increment: boolean) => {
    const newCount = increment ? numberOfSplits + 1 : Math.max(1, numberOfSplits - 1);
    setNumberOfSplits(newCount);

    const totalCents = Math.round(totalAmount * 100);
    const baseCents = Math.floor(totalCents / newCount);
    const remainderCents = totalCents - baseCents * newCount;

    const newSplits: PaymentSplit[] = [];
    for (let i = 0; i < newCount; i++) {
      const centsForThis = baseCents + (i < remainderCents ? 1 : 0);
      newSplits.push({
        id: String(i + 1),
        amount: centsForThis / 100,
        paymentMethod: 'TRANSFER',
        paid: false,
        showDueDateField: false,
        showInvoiceField: false
      });
    }
    setPaymentSplits(newSplits);
  };

  const handleSplitAmountChange = (splitId: string, amount: number) => {
    setPaymentSplits(splits => 
      splits.map(split => 
        split.id === splitId ? { ...split, amount } : split
      )
    );
  };

  const handleSplitDueDateChange = (splitId: string, date: Date) => {
    setPaymentSplits(splits => 
      splits.map(split => 
        split.id === splitId ? { ...split, dueDate: format(date, 'yyyy-MM-dd') } : split
      )
    );
  };

  const handleSplitPaymentMethodChange = (splitId: string, method: 'TRANSFER' | 'CARD' | 'CASH') => {
    setPaymentSplits(splits => 
      splits.map(split => 
        split.id === splitId ? { ...split, paymentMethod: method } : split
      )
    );
  };

  const handleToggleDueDateField = (splitId: string) => {
    setPaymentSplits(splits => 
      splits.map(split => 
        split.id === splitId ? { ...split, showDueDateField: !split.showDueDateField } : split
      )
    );
  };

  const handleMarkAsPaid = (splitId: string) => {
    setPaymentSplits(splits => 
      splits.map(split => 
        split.id === splitId ? { 
          ...split, 
          paid: true, 
          paidDate: format(new Date(), 'yyyy-MM-dd'),
          showInvoiceField: true
        } : split
      )
    );
  };

  const handleMarkAsUnpaid = (splitId: string) => {
    setPaymentSplits(splits => 
      splits.map(split => 
        split.id === splitId ? { 
          ...split, 
          paid: false, 
          paidDate: undefined,
          invoiceReference: undefined,
          showInvoiceField: false
        } : split
      )
    );
  };

  const handleToggleInvoiceField = (splitId: string) => {
    setPaymentSplits(splits => 
      splits.map(split => 
        split.id === splitId ? { ...split, showInvoiceField: !split.showInvoiceField } : split
      )
    );
  };

  const handleInvoiceReferenceChange = (splitId: string, reference: string) => {
    setPaymentSplits(splits => 
      splits.map(split => 
        split.id === splitId ? { ...split, invoiceReference: reference } : split
      )
    );
  };

  const handlePaidDateChange = (splitId: string, date: Date) => {
    setPaymentSplits(splits => 
      splits.map(split => 
        split.id === splitId ? { ...split, paidDate: format(date, 'yyyy-MM-dd') } : split
      )
    );
  };

  const calculateTotalSplits = () => {
    const sum = paymentSplits.reduce((acc, split) => acc + split.amount, 0);
    return Math.round(sum * 100) / 100;
  };

  const handleNextStep = () => {
    setCurrentStep(currentStep + 1);
  };

  const handlePreviousStep = () => {
    setCurrentStep(Math.max(1, currentStep - 1));
  };

  const handleSave = async () => {
    if (!orderId) return;
    
    try {
      const request: CreatePaymentPlanRequest = {
        totalAmount,
        currency,
        description: `Payment plan for ${itemName || 'order'}`,
        paymentSplits: paymentSplits.map(split => ({
          amount: split.amount,
          dueDate: split.dueDate,
          paymentMethod: split.paymentMethod,
          paid: split.paid || false,
          paidDate: split.paidDate,
          invoiceReference: split.invoiceReference
        }))
      };

      if (existingPaymentPlan) {
        // Update existing plan
        const updateRequest: UpdatePaymentPlanRequest = {
          totalAmount,
          currency,
          description: request.description,
          paymentSplits: paymentSplits.map(split => ({
            id: split.id ? Number(split.id) : undefined,
            amount: split.amount,
            dueDate: split.dueDate,
            paymentMethod: split.paymentMethod,
            paid: split.paid || false,
            paidDate: split.paidDate,
            invoiceReference: split.invoiceReference
          }))
        };
        await PaymentPlansAPI.updatePaymentPlan(orderId, updateRequest);
      } else {
        // Create new plan
        await PaymentPlansAPI.createPaymentPlan(orderId, request);
      }
      
      setIsOpen(false);
      setCurrentStep(1);
      setIsEditMode(false);
      await fetchPaymentPlan();
      onPaymentPlanUpdated?.();
    } catch (err: any) {
      setError(err.response?.data?.message || t('payment_plan.errors.failed_to_save'));
    }
  };

  const renderStep1 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          {existingPaymentPlan ? t('payment_plan.step1.edit.title') : t('payment_plan.step1.create.title')}
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          {existingPaymentPlan 
            ? t('payment_plan.step1.edit.description')
            : t('payment_plan.step1.create.description')
          }
        </p>
      </div>
      
      <div className="bg-gray-50 p-4 rounded-lg">
        <p className="text-sm font-medium text-gray-700 mb-2">
          {t('payment_plan.item')}: {defaultData?.itemName || itemName || 'Wedding Dress'}
        </p>
        <PriceField
          label={t('payment_plan.total_amount')}
          currency={currency}
          price={totalAmount}
          currencies={currencies}
          onCurrencyChange={handleCurrencyChange}
          onPriceChange={handlePriceChange}
        />
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          {existingPaymentPlan ? t('payment_plan.step2.edit.title') : t('payment_plan.step2.create.title')}
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          {existingPaymentPlan 
            ? t('payment_plan.step2.edit.description')
            : t('payment_plan.step2.create.description')
          }
        </p>
      </div>
      
      <div className="bg-gray-50 p-4 rounded-lg">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-700">
            {t('payment_plan.number_of_payments')}:
          </span>
          <div className="flex items-center space-x-3">
            <Tooltip content={t('payment_plan.decrease_payments') || 'Remove payment'} position="bottom">
              <ButtonV2
                onClick={() => handleNumberOfSplitsChange(false)}
                variant="outline"
                size="sm"
                disabled={numberOfSplits <= 1}
              >
                <Minus className="h-4 w-4" />
              </ButtonV2>
            </Tooltip>
            <span className="text-lg font-semibold min-w-[2rem] text-center">
              {numberOfSplits}
            </span>
            <Tooltip content={t('payment_plan.increase_payments') || 'Add payment'} position="bottom">
              <ButtonV2
                onClick={() => handleNumberOfSplitsChange(true)}
                variant="outline"
                size="sm"
              >
                <Plus className="h-4 w-4" />
              </ButtonV2>
            </Tooltip>
          </div>
        </div>
        
        <div className="mt-4 p-3 bg-white rounded border">
          <p className="text-sm text-gray-600">
            {t('payment_plan.split_preview')}:
          </p>
          <div className="mt-2 space-y-1">
            {paymentSplits.map((split, index) => (
              <div key={split.id} className="flex justify-between text-sm">
                <span>{t('payment_plan.payment')} {index + 1}:</span>
                <span className="font-medium">
                  {split.amount.toFixed(2)} {currency}
                </span>
              </div>
            ))}
            <div className="border-t pt-2 mt-2">
              <div className="flex justify-between text-sm font-semibold">
                <span>{t('payment_plan.total')}:</span>
                <span className={calculateTotalSplits() === totalAmount ? 'text-green-600' : 'text-red-600'}>
                  {calculateTotalSplits().toFixed(2)} {currency}
                </span>
              </div>
              {calculateTotalSplits() !== totalAmount && (
                <p className="text-xs text-red-500 mt-1">
                  {t('payment_plan.total_mismatch')}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          {existingPaymentPlan ? t('payment_plan.step3.edit.title') : t('payment_plan.step3.create.title')}
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          {existingPaymentPlan 
            ? t('payment_plan.step3.edit.description')
            : t('payment_plan.step3.create.description')
          }
        </p>
      </div>
      
      <div className="space-y-4">
        {paymentSplits.map((split, index) => (
          <div key={split.id} className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-gray-900">
                {t('payment_plan.payment')} {index + 1}
              </h4>
              <div className="flex items-center space-x-2">
                {!split.paid && (
                  <Tooltip content={t('payment_plan.mark_as_paid') || 'Mark as paid'} position="bottom">
                    <button
                      onClick={() => handleMarkAsPaid(split.id)}
                      className="flex items-center space-x-1 px-2 py-1 rounded-full hover:bg-green-200 transition-colors group"
                    >
                      <Check className="h-4 w-4 text-green-600 group-hover:text-green-700" />
                      <span className="hidden sm:inline text-xs text-green-600 group-hover:text-green-700">
                        {t('payment_plan.mark_as_paid')}
                      </span>
                    </button>
                  </Tooltip>
                )}
                {split.paid && (
                  <div className="flex items-center space-x-2">
                    <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                      {t('payment_plan.paid')}
                    </span>
                    <Tooltip content={t('payment_plan.mark_as_unpaid') || 'Mark as unpaid'} position="bottom">
                      <button
                        onClick={() => handleMarkAsUnpaid(split.id)}
                        className="p-1 rounded-full hover:bg-red-200 transition-colors group"
                      >
                        <X className="h-4 w-4 text-red-600 group-hover:text-red-700" />
                      </button>
                    </Tooltip>
                  </div>
                )}
              </div>
            </div>
            
            <div className="space-y-3">
              {/* Amount and Payment Method - Inline */}
              <div className="flex space-x-3">
                <div className="flex-1">
                  <InputNumber
                    label={t('payment_plan.amount') || 'Amount'}
                    onChange={(e) => handleSplitAmountChange(split.id, Number(e.currentTarget.value))}
                    defaultValue={split.amount}
                    isDisabled={false}
                    validationFn={(value) => value >= 0}
                    errorMessage="errors.minimum_zero"
                  />
                </div>
                <div className="flex-1">
                  <SelectInput
                    label={t('payment_plan.payment_method') || 'Payment Method'}
                    values={paymentMethods}
                    onChange={(e) => handleSplitPaymentMethodChange(split.id, e.target.value as 'TRANSFER' | 'CARD' | 'CASH')}
                    defaultValue={split.paymentMethod}
                    isDisabled={false}
                  />
                </div>
              </div>

              {/* Due Date Assignment */}
              {!split.showDueDateField ? (
                <TextOptionalActionButton
                  text={t('payment_plan.assign_due_date') || 'Assign a due date'}
                  onClick={() => handleToggleDueDateField(split.id)}
                  disabled={false}
                  actionName="Assign Due Date"
                  icon={<Calendar className="h-4 w-4" />}
                  variant="blue"
                />
              ) : (
                <div>
                  <CustomDatePicker
                    label={t('payment_plan.due_date') || 'Due Date'}
                    value={split.dueDate}
                    onChange={(date) => handleSplitDueDateChange(split.id, date)}
                    isDisabled={false}
                  />
                </div>
              )}

              {/* Paid Date - Only show if marked as paid */}
              {split.paid && (
                <div>
                  <CustomDatePicker
                    label={t('payment_plan.paid_on') || 'Paid on'}
                    value={split.paidDate}
                    onChange={(date) => handlePaidDateChange(split.id, date)}
                    isDisabled={false}
                    disablePast={false}
                  />
                </div>
              )}

              {/* Invoice Reference - Only show if paid */}
              {split.paid && (
                <div>
                  {!split.showInvoiceField ? (
                    <TextOptionalActionButton
                      text={split.invoiceReference ? t('payment_plan.edit_invoice_reference') || 'Edit invoice reference' : t('payment_plan.add_invoice_reference') || 'Add invoice reference'}
                      onClick={() => handleToggleInvoiceField(split.id)}
                      disabled={false}
                      actionName={split.invoiceReference ? "Edit Invoice Reference" : "Add Invoice Reference"}
                      icon={null}
                      variant="blue"
                    />
                  ) : (
                    <div>
                      <InputShortText
                        label={t('payment_plan.invoice_reference') || 'Invoice Reference'}
                        onChange={(e) => handleInvoiceReferenceChange(split.id, e.currentTarget.value)}
                        defaultValue={split.invoiceReference || ''}
                        placeholder={t('payment_plan.invoice_placeholder') || 'INV 01/02/2025'}
                        isDisabled={false}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderSkeleton = () => (
    <div className="space-y-6">
      <div>
        <Skeleton className="h-6 w-48 mb-2" />
        <Skeleton className="h-4 w-96" />
      </div>
      <div className="space-y-4">
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    </div>
  );

  const renderExistingPaymentPlan = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {t('payment_plan.existing.title')}
          </h3>
          <p className="text-sm text-gray-600">
            {t('payment_plan.existing.description')}
          </p>
        </div>
        <ButtonV2
          onClick={() => setIsEditMode(!isEditMode)}
          variant="outline"
          size="sm"
        >
          {isEditMode ? (
            <>
              <Eye className="h-4 w-4 mr-2" />
              {t('payment_plan.view')}
            </>
          ) : (
            <>
              <Edit className="h-4 w-4 mr-2" />
              {t('payment_plan.modify')}
            </>
          )}
        </ButtonV2>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      <div className="bg-gray-50 p-4 rounded-lg">
        <div className="flex justify-between items-center mb-4">
          <h4 className="font-medium text-gray-900">
            {t('payment_plan.total_amount')}: {totalAmount} {currency}
          </h4>
          <span className={`text-xs px-2 py-1 rounded-full ${
            paymentPlanStatusToPillColor(existingPaymentPlan?.status) === 'blue' ? 'bg-blue-100 text-blue-800' :
            paymentPlanStatusToPillColor(existingPaymentPlan?.status) === 'green' ? 'bg-green-100 text-green-800' :
            paymentPlanStatusToPillColor(existingPaymentPlan?.status) === 'red' ? 'bg-red-100 text-red-800' :
            'bg-gray-100 text-gray-800'
          }`}>
            {t(paymentPlanStatusToTranslationKey(existingPaymentPlan?.status))}
          </span>
        </div>



        <div className="space-y-3">
          {paymentSplits.map((split, index) => (
            <div key={split.id} className="bg-white p-3 rounded border">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-sm">
                  {t('payment_plan.payment')} {index + 1}
                </span>
                <div className="flex items-center space-x-2">
                  {!split.paid && !isEditMode && (
                    <Tooltip content={t('payment_plan.mark_as_paid') || 'Mark as paid'} position="bottom">
                      <button
                        onClick={() => handleMarkAsPaid(split.id)}
                        className="flex items-center space-x-1 px-2 py-1 rounded-full hover:bg-green-200 transition-colors group"
                      >
                        <Check className="h-4 w-4 text-green-600 group-hover:text-green-700" />
                        <span className="text-xs text-green-600 group-hover:text-green-700">
                          {t('payment_plan.mark_as_paid')}
                        </span>
                      </button>
                    </Tooltip>
                  )}
                  {split.paid && (
                    <div className="flex items-center space-x-2">
                      <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full flex items-center">
                        <Check className="h-3 w-3 mr-1" />
                        {t('payment_plan.paid')}
                      </span>
                      {!isEditMode && (
                        <Tooltip content={t('payment_plan.mark_as_unpaid') || 'Mark as unpaid'} position="bottom">
                          <button
                            onClick={() => handleMarkAsUnpaid(split.id)}
                            className="p-1 rounded-full hover:bg-red-200 transition-colors group"
                          >
                            <X className="h-4 w-4 text-red-600 group-hover:text-red-700" />
                          </button>
                        </Tooltip>
                      )}
                    </div>
                  )}
                  <span className="text-sm font-medium">
                    {split.amount} {currency}
                  </span>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                <div>
                  <span className="font-medium">{t('payment_plan.method')}:</span> {t(paymentMethodTypeToTranslationKey(split.paymentMethod))}
                </div>
                {split.dueDate && (
                  <div>
                    <span className={`font-medium ${!split.paid && new Date(split.dueDate) < new Date() ? 'text-red-600' : ''}`}>
                      {t('payment_plan.due')}:
                    </span>
                    <span className={!split.paid && new Date(split.dueDate) < new Date() ? 'text-red-600' : ''}>
                      {split.dueDate}
                      {!split.paid && new Date(split.dueDate) < new Date() && (
                        <span className="ml-1 text-red-500">({t('payment_plan.overdue')})</span>
                      )}
                    </span>
                  </div>
                )}
              </div>
              
              {/* Payment Status Information */}
              {split.paid && (
                <div className="mt-2 p-2 bg-green-50 rounded border border-green-200">
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    {split.paidDate && (
                      <div>
                        <span className="font-medium text-green-700">{t('payment_plan.paid_on')}:</span>
                        <span className="text-green-600"> {split.paidDate}</span>
                      </div>
                    )}
                    {split.invoiceReference && (
                      <div>
                        <span className="font-medium text-green-700">{t('payment_plan.invoice_ref')}:</span>
                        <span className="text-green-600"> {split.invoiceReference}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Show editable fields for paid payments */}
              {split.paid && (
                <div className="mt-3 pt-3 border-t border-gray-200 space-y-2">
                  <div>
                    <CustomDatePicker
                      label={t('payment_plan.paid_on') || 'Paid on'}
                      value={split.paidDate}
                      onChange={(date) => handlePaidDateChange(split.id, date)}
                      isDisabled={false}
                      disablePast={false}
                    />
                  </div>
                  
                  {!split.showInvoiceField ? (
                    <TextOptionalActionButton
                      text={split.invoiceReference ? t('payment_plan.edit_invoice_reference') || 'Edit invoice reference' : t('payment_plan.add_invoice_reference') || 'Add invoice reference'}
                      onClick={() => handleToggleInvoiceField(split.id)}
                      disabled={false}
                      actionName={split.invoiceReference ? "Edit Invoice Reference" : "Add Invoice Reference"}
                      icon={null}
                      variant="blue"
                    />
                  ) : (
                    <div>
                      <InputShortText
                        label={t('payment_plan.invoice_reference') || 'Invoice Reference'}
                        onChange={(e) => handleInvoiceReferenceChange(split.id, e.currentTarget.value)}
                        defaultValue={split.invoiceReference || ''}
                        placeholder={t('payment_plan.invoice_placeholder') || 'INV 01/02/2025'}
                        isDisabled={false}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderCurrentStep = () => {
    // Show skeleton while loading
    if (isLoading) {
      return renderSkeleton();
    }

    // Show existing payment plan if found and not in edit mode
    if (existingPaymentPlan && !isEditMode) {
      return renderExistingPaymentPlan();
    }

    // Show create/edit flow
    switch (currentStep) {
      case 1:
        return renderStep1();
      case 2:
        return renderStep2();
      case 3:
        return renderStep3();
      default:
        return renderStep1();
    }
  };

  const getPrimaryButton = () => {
    // Show loading state
    if (isLoading) {
      return {
        label: t('global.loading') || 'Loading...',
        onClick: () => {},
        disabled: true
      };
    }

    // Show existing payment plan view
    if (existingPaymentPlan && !isEditMode) {
      return {
        label: t('payment_plan.save_changes') || 'Save Changes',
        onClick: handleSave
      };
    }

    // Show save button for create/edit flow
    if (currentStep === 3) {
      return {
        label: existingPaymentPlan ? t('payment_plan.update') || 'Update Payment Plan' : t('payment_plan.create') || 'Create Payment Plan',
        onClick: handleSave
      };
    }

    // Show next button for create/edit flow
    return {
      label: t('global.next') || 'Next',
      onClick: handleNextStep
    };
  };

  const getSecondaryButton = () => {
    // Show loading state
    if (isLoading) {
      return {
        label: t('global.cancel') || 'Cancel',
        onClick: () => setIsOpen(false)
      };
    }

    // Show existing payment plan view
    if (existingPaymentPlan && !isEditMode) {
      return {
        label: t('global.close') || 'Close',
        onClick: () => setIsOpen(false)
      };
    }

    // Show cancel/back buttons for create/edit flow
    if (currentStep === 1) {
      return {
        label: t('global.cancel') || 'Cancel',
        onClick: () => setIsOpen(false)
      };
    }
    return {
      label: t("global.previous") || 'Back',
      onClick: handlePreviousStep
    };
  };

  return (
    <SlideOver
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      aboveModal={aboveModal}
      title={existingPaymentPlan 
        ? (t('payment_plan.edit.title') || 'Edit Payment Plan')
        : (t('payment_plan.create.title') || 'Build Payment Plan')
      }
      primaryButton={getPrimaryButton()}
      secondaryButton={getSecondaryButton()}
    >
      {renderCurrentStep()}
    </SlideOver>
  );
};

export default PaymentPlanManager; 